import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";

// Data
import { useConfirm } from "../../../components/feedback";
import DataTableWithPagination from "../../../components/DataTable";
import ProjectDialog from "../../../components/GenericDialog";
import { sourceSystemService } from "../../../services/sourceSystemService";
import SourceSystemModel, { getSourceSystemCols } from "../model";
import createSourceSystemBody from "./components/CreateSourceSystemDialogBody";
import editSourceSystemBody from "./components/EditSourceSystemDialogBody";

function SourceSystems() {
  const { t } = useTranslation();
  const cols = useMemo(() => getSourceSystemCols(t), [t]);
  const datatable = useRef<any>(null);
  const createSourceSystemDialogRef = useRef<any>(null);
  const editSourceSystemDialogRef = useRef<any>(null);
  const confirm = useConfirm();

  // Global refresh listener - triggered when snackbar closes
  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (datatable.current) {
        datatable.current.refresh();
      }
    };

    window.addEventListener('refreshDataTable', handleGlobalRefresh);
    
    return () => {
      window.removeEventListener('refreshDataTable', handleGlobalRefresh);
    };
  }, []);

  const createNewSourceSystem = () => {
    if (createSourceSystemDialogRef.current) {
      createSourceSystemDialogRef.current.showDialog(SourceSystemModel.createDialogOpts(t));
    }
  };

  const createSourceSystemSubmitAction = (formJson: any) => {
    sourceSystemService.createSourceSystem({
      name: formJson.name,
      logo: formJson.logo || '',
      contacts: formJson.contacts ? JSON.parse(formJson.contacts) : [],
      sources: formJson.sources ? JSON.parse(formJson.sources) : [],
      status: formJson.status,
      customerId: formJson.customerId || undefined
    }).then((obj) => {
      SourceSystemModel
        .message(SourceSystemModel.SUCCESS, t("common.createRequest"), t("sourceSystems.createdSuccess"), Date.now)
        .then((o) => {
          showSnack(o);
          setTimeout(() => datatable.current.refresh(), 500);
        });
    }).catch((e: unknown) => {
      console.error(e);
    });
  };

  const editSourceSystemSubmitAction = (formJson: any) => {
    sourceSystemService.updateSourceSystem({
      id: formJson.id,
      name: formJson.name,
      logo: formJson.logo || '',
      contacts: formJson.contacts ? JSON.parse(formJson.contacts) : [],
      sources: formJson.sources ? JSON.parse(formJson.sources) : [],
      status: formJson.status,
      customerId: formJson.customerId || undefined
    }).then((obj) => {
      SourceSystemModel
        .message(SourceSystemModel.SUCCESS, t("common.updateRequest"), t("sourceSystems.updatedSuccess"), Date.now)
        .then((o) => {
          showSnack(o);
          setTimeout(() => datatable.current.refresh(), 500);
        });
    }).catch((e: unknown) => {
      console.error(e);
    });
  };

  const handleDelete = async (item: any) => {
    return new Promise((resolve) => {
      confirm({ description: t("sourceSystems.deleteConfirm", { name: item.name || item.id }) })
        .then(() => {
          sourceSystemService.deleteSourceSystem(item.id).then(() => resolve({}));
        })
        .then(() => {
          SourceSystemModel
            .message(SourceSystemModel.SUCCESS, t("common.deleteRequest"), t("sourceSystems.deletedSuccess"), Date.now)
            .then((opts) => {
              showSnack(opts);
              setTimeout(() => datatable.current.refresh(), 500);
            });
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    });
  };

  const handleEdit = async (item: any) => {
    if (editSourceSystemDialogRef.current) {
      editSourceSystemDialogRef.current.showDialog(SourceSystemModel.editDialogOpts(t), item);
    }
  };

  const showSnack = (opts: any) => {
    // Use global notification system
    window.dispatchEvent(new CustomEvent('showGlobalNotification', { detail: opts }));
  };

  return (
    <PageListContainer>
      <ProjectDialog ref={createSourceSystemDialogRef} submitAction={createSourceSystemSubmitAction} body={createSourceSystemBody} maxWidth="md" />
      <ProjectDialog ref={editSourceSystemDialogRef} submitAction={editSourceSystemSubmitAction} body={editSourceSystemBody} maxWidth="md" />

      <DataTableWithPagination
        ref={datatable}
        cols={cols}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        fetchUrl={"/source-system/read"}
        searchKey={"name"}
        headerActions={
          <PageCreateButton onClick={createNewSourceSystem}>{t("sourceSystems.new")}</PageCreateButton>
        }
      />
    </PageListContainer>
  );
}

export default SourceSystems;
